import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSession } from '@/context/SessionContext';
import { useLanguage } from '@/context/LanguageContext';
import {
  Home, Layers, LineChart, FileText, Users, PenTool, Star,
  FolderOpen, Info, Menu, X, ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const LearnerSidebar = () => {
  const { sessions, selectedSessionId, setSelectedSessionId } = useSession();
  const { t } = useLanguage();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const menuItems = [
    { id: 'accueil', label: 'Accueil', href: '/apprenant', icon: Home },
    { id: 'sequences', label: 'Séquences e-learning', href: '/apprenant/sequences', icon: Layers },
    { id: 'suivi', label: 'Suivi pédagogique', href: '/apprenant/suivi', icon: LineChart },
    { id: 'programme', label: 'Programme', href: '/apprenant/programme', icon: FileText },
    { id: 'apprenants', label: 'Apprenants', href: '/apprenant/apprenants', icon: Users },
    { id: 'emargements', label: 'Émargements', href: '/apprenant/emargements', icon: PenTool },
    { id: 'evaluations', label: 'Évaluations', href: '/apprenant/evaluations', icon: Star },
    { id: 'documents', label: 'Documents', href: '/apprenant/documents', icon: FolderOpen },
    { id: 'apropos', label: 'À propos', href: '/apprenant/apropos', icon: Info },
  ];

  const isActive = (href) => {
    if (href === '/apprenant') return location.pathname === '/apprenant';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      <nav className="flex-1 pt-4 px-3 space-y-0.5">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.id}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                active
                  ? "text-blue-700 font-semibold"
                  : "text-slate-600 hover:text-blue-700 hover:bg-blue-50/50"
              )}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid={`nav-${item.id}`}
            >
              <Icon className={cn("h-[18px] w-[18px]", active ? "text-blue-700" : "text-slate-400")} strokeWidth={active ? 2.5 : 1.5} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Session selector */}
      <div className="p-4 border-t border-slate-100 mt-auto">
        <p className="text-xs text-slate-400 mb-2">Accès à vos sessions :</p>
        <Select value={selectedSessionId || ''} onValueChange={setSelectedSessionId}>
          <SelectTrigger className="h-10 text-xs border-slate-200" data-testid="session-selector">
            <SelectValue placeholder="Sélectionner une session" />
          </SelectTrigger>
          <SelectContent>
            {(sessions || []).map((s) => (
              <SelectItem key={s.session_id || s.course_id} value={s.session_id || s.course_id}>
                {(s.title || s.course_title || 'Session').slice(0, 40)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        className="lg:hidden fixed top-5 left-4 z-50 w-9 h-9 bg-white border border-slate-200 rounded-lg flex items-center justify-center shadow-sm"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        data-testid="mobile-menu-toggle"
      >
        {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
      </button>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/30 z-40" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-[72px] left-0 bottom-0 w-64 bg-white border-r border-slate-100 z-40 transition-transform duration-200",
        isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )} data-testid="learner-sidebar">
        <NavContent />
      </aside>
    </>
  );
};

export default LearnerSidebar;
